import { useEffect, useRef, useState } from 'preact/hooks';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

/**
 * Captures `beforeinstallprompt` and exposes a single-use `install()`.
 * Browsers that never fire the event (or where the app is already
 * installed) must not break the control — `install()` no-ops safely rather
 * than throwing (FR-004).
 */
export function useInstallPrompt() {
  const [installed, setInstalled] = useState(false);
  const promptEventRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    function handler(e: Event) {
      e.preventDefault();
      promptEventRef.current = e as BeforeInstallPromptEvent;
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function install() {
    const event = promptEventRef.current;
    if (event) {
      // T041: a rejected prompt() must not surface as an unhandled rejection
      // — FR-004 requires the control degrade with no error.
      event.prompt().catch(() => {});
      promptEventRef.current = null;
    }
    setInstalled(true);
  }

  return { installed, install };
}
